/**
 * EntityLinkPicker — autocompletado server-side para campos personalizados de
 * tipo "vínculo a entidad" (entity_link). Busca por nombre (Cliente) o por
 * nombre/folio (Documento de venta/compra) a medida que se escribe.
 *
 * Guarda { id, label } de la entidad seleccionada vía onChange(id, label).
 */
import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'
import { Autocomplete, type AutocompleteItem } from './Autocomplete'

export type LinkEntity = 'customer' | 'sale_document' | 'supplier_invoice'

interface Props {
  linkEntity: LinkEntity
  valueId?: string
  valueLabel?: string
  onChange: (id: string, label: string) => void
  placeholder?: string
  required?: boolean
  disabled?: boolean
  /** Nombre accesible: el picker no tiene etiqueta propia, la pone quien lo usa. */
  'aria-label'?: string
}

const CONFIG: Record<LinkEntity, {
  url: string
  arrayKeys: string[]
  toLabel: (o: any) => string
  placeholder: string
}> = {
  customer: {
    url: '/sales/customers/',
    arrayKeys: ['customers', 'results'],
    toLabel: (c) => `${c.legal_name || c.name || '—'}${c.tax_id ? ` (${c.tax_id})` : ''}`,
    placeholder: 'Buscar cliente por nombre o RUT…',
  },
  sale_document: {
    url: '/sales/documents/',
    arrayKeys: ['documents', 'results'],
    toLabel: (d) => `${d.folio ? `#${d.folio}` : (d.id ? d.id.slice(0, 8) : '—')}${d.type_label ? ` · ${d.type_label}` : ''}${d.customer_name ? ` · ${d.customer_name}` : ''}`,
    placeholder: 'Buscar documento de venta por folio o cliente…',
  },
  supplier_invoice: {
    url: '/purchases/invoices/',
    arrayKeys: ['invoices', 'results'],
    toLabel: (i) => `${i.invoice_number || (i.id ? i.id.slice(0, 8) : '—')}${i.supplier_name ? ` · ${i.supplier_name}` : ''}`,
    placeholder: 'Buscar documento de compra por nº o proveedor…',
  },
}

function extractArray(data: any, keys: string[]): any[] {
  if (Array.isArray(data)) return data
  for (const k of keys) if (Array.isArray(data?.[k])) return data[k]
  if (Array.isArray(data?.results)) return data.results
  return []
}

export default function EntityLinkPicker({
  linkEntity, valueId, valueLabel, onChange, placeholder, required, disabled,
  'aria-label': ariaLabel,
}: Props) {
  const cfg = CONFIG[linkEntity]
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250)
    return () => clearTimeout(t)
  }, [query])

  const { data, isFetching } = useQuery({
    queryKey: ['entity-link-search', linkEntity, debounced],
    queryFn: async () => {
      const res = await apiClient.get(cfg.url, { params: { search: debounced, page_size: 20 } })
      return res.data
    },
    enabled: debounced.length >= 2,
    staleTime: 60 * 1000,
  })

  const items: AutocompleteItem[] = useMemo(() => {
    const arr = extractArray(data, cfg.arrayKeys)
    return arr.map((o: any) => ({ id: String(o.id), label: cfg.toLabel(o) }))
  }, [data, cfg])

  return (
    <div>
      <Autocomplete aria-label={ariaLabel}
        items={items}
        freeText
        value={valueLabel || ''}
        displayValue={valueLabel || ''}
        onChange={(text) => setQuery(text)}
        onSelect={(item) => onChange(item.id, item.label)}
        placeholder={placeholder || cfg.placeholder}
        required={required}
        disabled={disabled}
      />
      {debounced.length >= 2 && isFetching && (
        <p className="text-[11px] text-content-subtle mt-1">Buscando…</p>
      )}
      {debounced.length >= 2 && !isFetching && items.length === 0 && (
        <p className="text-[11px] text-content-subtle mt-1">Sin resultados.</p>
      )}
    </div>
  )
}
